import { useQuery } from '@tanstack/react-query';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';
import type { PresencaDiaria } from '../../types/models';
import { PRESENCAS } from '../routes';

/**
 * Hook para Presenças Diárias.
 * GET /escolas/:escolaId/presencas-diarias
 */
export function usePresencasDiarias(filters?: {
  data?: string;
  dataInicio?: string;
  dataFim?: string;
  alunoId?: string;
  turmaId?: string;
  /** Poll em modo simulação (acompanhar robôs RFID/chamada). */
  refetchIntervalMs?: number | false;
  /** false enquanto o relógio simulado ainda não sincronizou. */
  enabled?: boolean;
}) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const isSim = !!(useAuthStore((s) => s.user) as { isSimulation?: boolean } | null)?.isSimulation;
  const basePath = PRESENCAS.diarias(escolaId!, {});
  const refetchInterval =
    filters?.refetchIntervalMs !== undefined
      ? filters.refetchIntervalMs
      : isSim
        ? 15_000
        : false;

  const query = useQuery({
    queryKey: ['presencas-diarias', escolaId, filters],
    queryFn: async () => {
      // Backend DTO: dataReferencia + paginação { items, total }
      const params: Record<string, string | number | undefined> = {
        alunoId: filters?.alunoId,
        turmaId: filters?.turmaId,
        // Dia letivo completo (escola média ~300–700 alunos)
        limite: filters?.data || filters?.dataInicio ? 2000 : 200,
        pagina: 1,
      };
      if (filters?.data) {
        params.dataReferencia = filters.data;
      }
      if (filters?.dataInicio) params.dataInicio = filters.dataInicio;
      if (filters?.dataFim) params.dataFim = filters.dataFim;
      const { data } = await api.get(basePath, { params });
      const raw: any[] = Array.isArray(data)
        ? data
        : (data?.items ?? data?.data ?? []);
      return raw.map(mapPresencaDiaria);
    },
    enabled: !!escolaId && (filters?.enabled ?? true),
    refetchInterval,
    refetchOnWindowFocus: true,
  });

  return {
    presencas: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Normaliza item Prisma → modelo UI (data/status). */
function mapPresencaDiaria(p: any): PresencaDiaria {
  return {
    ...p,
    status: (p.status ?? p.statusPresencaDiaria) as PresencaDiaria['status'],
    data: p.data ?? p.dataReferencia,
    turmaId: p.turmaId ?? p.turma?.id,
  } as PresencaDiaria;
}

/** Extrai YYYY-MM-DD sem deslocar fuso (UTC midnight → dia local errado). */
export function presencaDateParts(value?: string | null): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const ymd = String(value).includes('T') ? String(value).slice(0, 10) : String(value).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return { y, m: mo, d };
}

/** Data de presença em pt-BR (calendário local). */
export function formatPresencaDate(value?: string | null): string {
  const parts = presencaDateParts(value);
  if (!parts) return '—';
  return new Date(parts.y, parts.m - 1, parts.d).toLocaleDateString('pt-BR');
}

/** Dia do mês para chips; nunca retorna NaN. */
export function presencaDayNumber(value?: string | null): string {
  const parts = presencaDateParts(value);
  return parts ? String(parts.d) : '—';
}

/**
 * Hook para frequência acumulada de um aluno específico.
 * Calcula % alinhado ao backend `calcularStats` (PRESENTE|ATRASADO|PARCIAL).
 */
export function useFrequenciaAluno(alunoId?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);

  const empty = {
    presencas: [] as PresencaDiaria[],
    totalDias: 0,
    diasPresente: 0,
    diasAusente: 0,
    diasParcial: 0,
    percentual: 0,
    emRisco: false,
  };

  const query = useQuery({
    queryKey: ['frequencia-aluno', escolaId, alunoId],
    queryFn: async () => {
      const { data } = await api.get(`/escolas/${escolaId}/presencas-diarias`, {
        params: { alunoId, limite: 500 },
      });
      const raw: any[] = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
      const presencas = raw.map(mapPresencaDiaria);

      const totalDias = presencas.length;
      const diasPresente = presencas.filter(
        (p) =>
          p.status === 'PRESENTE' ||
          p.status === 'ATRASADO' ||
          p.status === 'PARCIAL' ||
          p.status === 'SAIU',
      ).length;
      // INCONSISTENTE: entrou (RFID) mas falta na chamada — conta como ausência
      const diasAusente = presencas.filter(
        (p) => p.status === 'AUSENTE' || p.status === 'INCONSISTENTE',
      ).length;
      const diasParcial = presencas.filter((p) => p.status === 'PARCIAL').length;
      const percentual =
        totalDias > 0 ? Math.round((diasPresente / totalDias) * 100) : 0;
      const emRisco = totalDias > 0 && percentual < 75;

      return {
        presencas,
        totalDias,
        diasPresente,
        diasAusente,
        diasParcial,
        percentual,
        emRisco,
      };
    },
    enabled: !!escolaId && !!alunoId,
  });

  return {
    frequencia: query.data ?? empty,
    isLoading: query.isLoading,
    error: query.error,
  };
}
